import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, SITE_URL } from "../lib/api";
import Seo from "../components/Seo";
import JobCard from "../components/JobCard";

function prettify(slug) {
  return (slug || "").split("-").filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

export default function TownJobs() {
  const { town } = useParams();
  const name = prettify(town);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!name) return;
    setLoading(true);
    api.get("/jobs", { params: { location: name, limit: 50 } })
      .then((res) => setData(res.data))
      .catch(() => setData({ items: [], total: 0, page: 1, pages: 1 }))
      .finally(() => setLoading(false));
  }, [name]);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="town-jobs-page">
      <Seo
        title={`Jobs near ${name} | HiredToday`}
        description={`Live jobs near ${name}, reviewed before they go live. Browse roles in ${name} and nearby towns and apply free on HiredToday.`}
        path={`/jobs/near/${town}`}
        noindex={!loading && data.total === 0}
      />
      {data.items.length > 0 && (
        <script type="application/ld+json" data-testid="town-itemlist-schema">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": data.items.map((j, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "url": `${SITE_URL}/jobs/${j.slug}`,
              "name": j.title,
            })),
          })}
        </script>
      )}
      <div className="mb-8">
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-black text-zinc-950">Jobs near {name}</h1>
        <p className="mt-3 text-zinc-600" data-testid="town-jobs-count">
          {loading ? "Searching…" : `${data.total} ${data.total === 1 ? "role" : "roles"} live in and around ${name}`}
        </p>
      </div>

      {loading ? (
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={`town-skel-${i}`} className="border border-zinc-200 rounded-md p-6 animate-pulse">
              <div className="h-5 bg-zinc-100 rounded w-1/3 mb-3" />
              <div className="h-4 bg-zinc-100 rounded w-1/4" />
            </div>
          ))}
        </div>
      ) : data.items.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center" data-testid="town-jobs-empty">
          <p className="font-display text-xl font-bold text-zinc-900">No live roles near {name} right now.</p>
          <p className="mt-2 text-sm text-zinc-500">New listings land daily. Browse everything, or save a search and we'll email you the moment one matches.</p>
          <Link to="/jobs" data-testid="town-browse-all"
            className="mt-6 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-zinc-800 transition-colors duration-200">
            Browse all jobs
          </Link>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="town-jobs-results">
          {data.items.map((job, i) => <JobCard key={job.id} job={job} index={i} />)}
        </div>
      )}

      <div className="mt-14 bg-zinc-950 rounded-md p-8 sm:p-10 text-center" data-testid="town-employer-cta">
        <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400">For employers</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mt-2">Hiring in {name}?</h2>
        <p className="mt-3 text-sm text-zinc-400 max-w-xl mx-auto leading-relaxed">
          Post your role on HiredToday and reach candidates searching {name} and the surrounding towns. Every listing is reviewed by our team before it goes live.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/post-a-job" state={{ prefill: { location: name } }} data-testid="town-post-job-cta"
            className="inline-flex items-center justify-center gap-2 bg-white text-zinc-950 text-sm font-semibold px-6 py-3 rounded-md hover:bg-zinc-200 transition-colors duration-200">
            Post a job in {name}
          </Link>
          <Link to="/pricing" data-testid="town-pricing-cta"
            className="inline-flex items-center justify-center gap-2 border border-zinc-700 text-zinc-300 text-sm font-semibold px-6 py-3 rounded-md hover:border-zinc-500 hover:text-white transition-colors duration-200">
            See pricing
          </Link>
        </div>
        <p className="mt-4 text-xs text-zinc-500">First listing is free. Paid and urgent listings include Location Boost across nearby towns.</p>
      </div>
    </main>
  );
}
